/** Convert widget smart value placeholder into its value, or return the parameter if not a smart value placeholder.
 * @param val The value to check and optionally convert
 * */
export function widgetConvertSmartValue(val) {
    switch (val) {
        case '{#auto-pov-location#}':
            return Laravel.default_location;
        case '{#auto-user-language#}':
            return Laravel.activeLanguage;
        case '{#auto-project-primary-color#}':
            return Laravel.primary_color;
        case '{#auto-project-secondary-color#}':
            return Laravel.secondary_color;
        default:
            return val;
    }
}

/**
 * Get a widget setting ('WIDGET_*') value from {@link elementProperties} or {@link widgetSettings} (where first defined) and convert the value if a smart value, or null if undefined.
 * */
export function getWidgetSettingValueConverted(widgetSettings, elementProperties = {}, settingKey) {
    const value = elementProperties[settingKey] ?? widgetSettings[settingKey].default;
    return value == undefined ? null : widgetConvertSmartValue(value);
}

/**
 * Get an object with actualised widget settings
 * @param widgetSettings {Record} Object with a property 'WIDGET_*' for each possible widget setting. Each entry may contain a 'default' property, or it may not
 * @param elementProperties {Record?} Optional object with properties of element (modified by user), which may contain some (or all) of entries of {@link widgetSettings} and the set values
 * @returns {Record} Object with a property 'WIDGET_*' for each setting (which is defined either in {@link widgetSettings}['WIDGET_*'].default, or in {@link elementProperties}['WIDGET_*']), or empty object if {@link widgetSettings} is not defined
 */
export function widgetSettingsAndPropertiesToStructure(widgetSettings, elementProperties = {}) {
    if (!widgetSettings) {
        return {};
    }

    const structure = {};

    for (const settingKey in widgetSettings) {
        const convertedValue = getWidgetSettingValueConverted(widgetSettings, elementProperties, settingKey);
        if (convertedValue == undefined) {
            continue;
        }
        structure[settingKey] = convertedValue;
    }

    return structure;
}

/**
 * Get a widget query_string with actualised widget settings
 * @param widgetSettings {Record} Object with a property 'WIDGET_*' for each possible widget setting. Each entry may contain a 'default' property, or it may not
 * @param elementProperties {Record?} Optional object with properties of element (modified by user), which may contain some (or all) of entries of {@link widgetSettings} and the set values
 * @returns {string} query_string
 */
export function widgetSettingsAndPropertiesToQueryString(widgetSettings, elementProperties = {}) {
    if (!widgetSettings) {
        return '';
    }

    const queryParams = [];

    for (const settingKey in widgetSettings) {
        const widgetSetting = widgetSettings[settingKey];
        const convertedValue = getWidgetSettingValueConverted(widgetSettings, elementProperties, settingKey);
        if (convertedValue == undefined) {
            continue;
        }
        queryParams.push(`${encodeURIComponent(widgetSetting.exportfield)}=${encodeURIComponent(convertedValue)}`);
    }

    return queryParams.length ? `?${queryParams.join('&')}` : '';
}
